import _ from "lodash";
import { action, flow, observable, ObservableMap } from "mobx";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { ITextAndTranslation } from "./ITextAndTranslation";
import { CvpLocale } from "../constants/CvpLocale";
import { useEvaluatedLocale } from "./useEvaluatedLocale";
import { useComputed } from "../utils/useComputed";
import { getSourceMap } from "./getSourceMap";
import { getTranslationMap } from "./getTranslationMap";
import { loc } from "./loc";
import { LocContext } from "./LocContext";
import { split, SplitReplacement } from "./split";
import { splitStr, SplitStrReplacement } from "./splitStr";

interface IGetTranslationsForLocaleResponse {
  locale: CvpLocale;
  stringContext: LocContext;
  items: ITextAndTranslation[];
}
async function getTranslationsForLocale(
  stringContext: LocContext,
  locale: CvpLocale
): Promise<IGetTranslationsForLocaleResponse> {
  const translationsPromise =
    locale !== CvpLocale.nb ? getTranslationMap(stringContext, locale) : null;

  const sourcesPromise = getSourceMap(stringContext, CvpLocale.nb);

  let [translations, sources] = await Promise.all([
    translationsPromise,
    sourcesPromise,
  ]);

  if (locale === CvpLocale.nb) {
    translations = sources;
  }

  // If we are missing translations, we will fetch the english translations too
  let englishTranslations: { [frontendId: string]: any } = {};

  let isMissingTranslations = Object.keys(sources).some(
    (key) => !translations[key]
  );

  if (isMissingTranslations && locale !== CvpLocale.en) {
    englishTranslations = await getTranslationMap(stringContext, CvpLocale.en);
  }
  // --------------------------------------------

  const items: ITextAndTranslation[] = [];
  Object.keys(sources).forEach((key) => {
    items.push({
      frontendId: key,
      text: sources[key],
      translated: translations[key] ?? "",
      englishText: englishTranslations[key] ?? "",
    });
  });

  return {
    locale,
    stringContext,
    items,
  };
}

// Handles fetching of translations and puts them in the global mobx store locStoreInstancesObservable
// we could do this without mobx but it's a lot of work to reimplement the same reactive behavior
export const TranslationProvider = observer(() => {
  const evaluatedLocale = useEvaluatedLocale();
  const instances = useComputed(() =>
    Object.values(locStoreInstancesObservable.get())
  );

  const stringContexts = instances.get.map(
    (instance) => instance.stringContext
  );

  useEffect(() => {
    flow(function* () {
      if (!evaluatedLocale) return;
      const _locale = evaluatedLocale as CvpLocale;

      const promises: Promise<IGetTranslationsForLocaleResponse>[] = [];
      const instancesThatNeedTranslations = instances.get.filter((i) => {
        if (i.gotTranslationsForLocale === evaluatedLocale) return false;
        if (i.waitingForTranslationsForLocale === evaluatedLocale) return false;
        return true;
      });

      const stringContextsThatNeedTranslations = _.uniq(
        instancesThatNeedTranslations.map((i) => i.stringContext)
      );

      stringContextsThatNeedTranslations.forEach((stringContext) => {
        promises.push(getTranslationsForLocale(stringContext, _locale));
      });

      instancesThatNeedTranslations.forEach((i) => {
        i.waitingForTranslationsForLocale = _locale;
      });

      const translationResponses: IGetTranslationsForLocaleResponse[] =
        yield Promise.all(promises);

      translationResponses.forEach((translationResponse) => {
        const instance = instances.get.find(
          (i) => i.stringContext === translationResponse.stringContext
        );

        if (!instance) return;

        instance.translations.replace(
          translationResponse.items.reduce((acc, item) => {
            acc[item.frontendId] = item;
            return acc;
          }, {} as { [frontendId: string]: ITextAndTranslation })
        );

        instance.gotTranslationsForLocale = translationResponse.locale;
        instance.waitingForTranslationsForLocale = null;
        instance.onLocaleLoadedCallbacks.forEach((cb) =>
          cb(translationResponse.locale)
        );
      });
    })();
  }, [stringContexts.join(","), evaluatedLocale]);

  return null;
});

interface LocStoreInstances {
  [stringContext: string]: LocStoreInstance;
}
const locStoreInstances: LocStoreInstances = {};
const locStoreInstancesObservable = observable.box<LocStoreInstances>({});

/**
 * Get object used for translating text.
 * Use useLocStore instead when possible
 */
export const getLocStore = (stringContext: LocContext): LocStoreInstance => {
  if (!stringContext) throw new Error("getLocStore requires stringContext");

  let instance = locStoreInstances[stringContext];

  if (!instance) {
    instance = observable({
      translations: observable.map<string, ITextAndTranslation>(),
      onLocaleLoadedCallbacks: [],
      onLocaleLoaded: (cb: (locale: CvpLocale) => void) => {
        instance.onLocaleLoadedCallbacks.push(cb);

        // Return a function that will remove the callback from the list
        return () => {
          instance.onLocaleLoadedCallbacks =
            instance.onLocaleLoadedCallbacks.filter((c) => c !== cb);
        };
      },
      getTranslation: (
        frontendId: string,
        text: string,
        stringContext: string
      ) => {
        const found = instance.translations.get(frontendId);

        if (found) return found;

        return null;
      },
      gotTranslationsForLocale: null,
      waitingForTranslationsForLocale: null,
      id: (id: string) => getLocStoreId(id, stringContext, instance),
      stringContext,
    });

    locStoreInstances[stringContext] = instance;

    setTimeout(
      action(() => {
        locStoreInstancesObservable.set(locStoreInstances);
      })
    );
  }

  return locStoreInstances[stringContext];
};

/**
 * Get object used for translating text
 */
export const useLocStore = (locContext: LocContext): LocStoreInstance => {
  const instance = useMemo(() => getLocStore(locContext), []);

  // trigger rerender when translations are loaded
  const [_, setRerender] = useState("");

  useEffect(() => {
    return instance.onLocaleLoaded((locale) => {
      setRerender((r) => locale);
    });
  }, []);

  return instance;
};

export interface LocStoreInstance {
  translations: ObservableMap<string, ITextAndTranslation>;
  getTranslation: (
    frontendId: string,
    text: string,
    stringContext: string
  ) => ITextAndTranslation;
  stringContext: LocContext;
  gotTranslationsForLocale: CvpLocale;
  waitingForTranslationsForLocale: CvpLocale;
  onLocaleLoaded: (cb: (locale: CvpLocale) => void) => () => void;
  onLocaleLoadedCallbacks: ((locale: CvpLocale) => void)[];
  id: (id: string) => {
    loc: (s: string, searchWords?: string[]) => any;
    split: (
      s: string,
      replacements: SplitReplacement,
      searchWords?: string[]
    ) => any;
    splitStr: (
      s: string,
      replacements: SplitStrReplacement,
      searchWords?: string[]
    ) => any;
  };
}

const getLocStoreId = (
  id: string,
  stringContext: string,
  locStoreInstance: LocStoreInstance
) => {
  return {
    loc: (s: string, searchWords?: string[]) => {
      return loc(s, searchWords, stringContext, id, locStoreInstance);
    },
    split: (
      s: string,
      replacements: SplitReplacement,
      searchWords?: string[]
    ) => {
      return split(
        s,
        replacements,
        searchWords,
        stringContext,
        id,
        locStoreInstance
      );
    },
    splitStr: (
      s: string,
      replacements: SplitStrReplacement,
      searchWords?: string[]
    ) => {
      return splitStr(
        s,
        replacements,
        searchWords,
        stringContext,
        id,
        locStoreInstance
      );
    },
  };
};
